import type { ReactNode } from 'react'
import { cn } from './cn'

/**
 * Pastille d'état.
 *
 * Reprend les variantes du design system (`lav`, `ink`, `outline`, `tag`) et
 * ajoute les trois états que l'app manipule en permanence et que le DS, pensé
 * pour le marketing, ne couvrait pas : `ok`, `neutral`, `alert`.
 *
 * `alert` puise dans les couleurs système corail — hors charte, réservées aux
 * retards et aux alertes. Ne pas l'employer pour attirer l'œil sur autre chose :
 * si tout est en alerte, plus rien ne l'est.
 */

type Variant = 'lav' | 'ink' | 'outline' | 'tag' | 'ok' | 'neutral' | 'alert'

const VARIANTS: Record<Variant, string> = {
  lav: 'bg-lavender text-ink',
  ink: 'bg-ink text-cream',
  outline: 'bg-transparent text-ink border border-hairline-strong',
  tag: 'bg-lavender/[0.18] text-lavender-deep',
  ok: 'bg-lavender-soft text-ink',
  neutral: 'bg-cream-warm text-ink-soft',
  alert: 'bg-alert-bg text-alert-fg',
}

export function Badge({
  variant = 'neutral',
  dot = false,
  children,
  className,
}: {
  variant?: Variant
  /** Point de statut devant le libellé, comme dans le design system. */
  dot?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1',
        'text-[11.5px] font-bold tracking-[0.02em] whitespace-nowrap',
        VARIANTS[variant],
        className,
      )}
    >
      {dot && <span className="size-1.5 rounded-pill bg-current" aria-hidden="true" />}
      {children}
    </span>
  )
}
